import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TenantService } from '../../../../Services/tenant.service';
import {
  FormatViewModel,
  ImportService,
  NewImportViewModel,
  NodeError,
  RawRow,
  ValidationResult,
} from '../Services/import.service';
import { MessageJobSeverity } from '@app/Enums';
import { ImportType } from '../Enums';
import { MatCard, MatCardTitle, MatCardSubtitle, MatCardContent } from '@angular/material/card';
import { JobLiveViewComponent } from '../../../../Jobs/job.liveview.component';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ListItemsImportComponent } from './list-items-import/list-items-import.component';

@Component({
    selector: 'new-import',
    templateUrl: './new.import.component.html',
    styleUrls: ['./new.import.component.css', '../../../../provisioning.css'],
    imports: [MatCard, MatCardTitle, MatCardSubtitle, MatCardContent, JobLiveViewComponent, MatSelect, FormsModule, MatOption, MatButton, MatIcon, ListItemsImportComponent]
})
export class NewImportComponent implements OnInit, OnDestroy {
  importType: string;
  viewModel: NewImportViewModel;
  selectedFormat: FormatViewModel;
  filename: string;
  busy: boolean;
  busyMessage: string;
  fileError: string;
  importResult: ValidationResult;
  selectedModuleId: string = '';
  selectedStateId: string;
  error: NodeError;
  provisioningJob: string;
  tenantName: string;
  additionalValues = {};
  result: any;
  private destroy$ = new Subject<void>();

  constructor(
    private tenantService: TenantService,
    private activatedRoute: ActivatedRoute,
    private importService: ImportService
  ) { }

  ngOnInit(): void {
    const tenant = this.tenantService.selectedTenant;
    this.importType = this.activatedRoute.snapshot.params.type;
    this.importService
      .getNewViewModel(this.importType, tenant.TenantId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((r) => {
        this.viewModel = r;
      });
    this.tenantName = tenant.Value;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  jobComplete(event: MessageJobSeverity): void {
    if (event === MessageJobSeverity.Success) {
      console.log('File imported successfully');
    }
  }

  setModule(e: any): void {
    this.resetViewModel();

    if (e.value) {
      this.viewModel.SelectedModuleId = e.value;
      this.importService
        .getFormats(
          this.importType,
          this.tenantService.selectedTenant.TenantId,
          e.value
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe((r) => (this.viewModel.Formats = r));

      this.importService
        .getImportStates(this.tenantService.selectedTenant.TenantId, e.value)
        .pipe(takeUntil(this.destroy$))
        .subscribe((r) => {
          this.viewModel.AvailableStates = r;
        });
    }
  }

  setButton(e: any): void {
    if (e.value) {
      this.viewModel.SelectedStateId = e.value;
    }
  }

  previewFile(e: any): void {
    const files: FileList = e.target.files;
    const file = files[0];
    this.filename = file.name;
    this.busy = true;
    this.busyMessage = 'Uploading file';
    this.fileError = null;
    this.importService
      .uploadFile(
        file,
        this.importType,
        this.selectedFormat,
        this.viewModel.Id,
        this.selectedModuleId,
        this.filename
      )
      .pipe(
        map((r) => r),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (data) => {
          this.busy = false;
          this.viewModel = data as NewImportViewModel;
        },
        (error) => {
          console.log(error);
          this.busy = false;
          this.fileError = 'File was not the correct format';
        }
      );
  }

  validate(): void {
    this.busy = true;
    this.busyMessage = 'Validating the structure of the file';
    this.importService
      .validate(this.viewModel.Id, this.selectedModuleId, this.selectedStateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((r) => {
        this.viewModel = r;
        this.busy = false;
      });
  }

  getFieldClass(r: RawRow, i: number): string {
    return r.Errors.filter((e) => e.ColumnIndex === i).length > 0
      ? 'error'
      : '';
  }

  showAsError(f: boolean): string {
    return f ? 'error' : '';
  }

  hasErrors(): boolean {
    if (!this.viewModel) {
      return false;
    }
    return (
      (this.viewModel.ErrorRows && this.viewModel.ErrorRows.length > 0) ||
      (this.viewModel.NodeErrors && this.viewModel.NodeErrors.length > 0) ||
      this.viewModel.Failed
    );
  }

  import(): void {
    this.busy = true;
    this.busyMessage = `Importing ${this.viewModel.Title}...`;

    if (this.importType === ImportType.HistoricalData) {
      this.additionalValues = {
        moduleId: this.selectedModuleId,
        importState: this.selectedStateId,
      };
    }

    this.importService
      .beginImport(this.viewModel.Id, this.filename, this.additionalValues)
      .pipe(takeUntil(this.destroy$))
      .subscribe((r) => {
        this.importResult = r;
        this.provisioningJob = r.Model;
        this.busy = false;
      });
  }

  showFileFormatCard(): boolean {
    if (
      this.importType !== ImportType.HistoricalData &&
      !this.viewModel.Top5Rows
    ) {
      return true;
    }
    if (this.importType === ImportType.HistoricalData) {
      return (
        this.viewModel.SelectedModuleId &&
        this.viewModel.SelectedStateId &&
        !this.viewModel.Top5Rows
      );
    }
    return false;
  }

  showModuleStateCard(): boolean {
    return this.importType === ImportType.HistoricalData;
  }

  resetViewModel(): void {
    this.selectedFormat = null;
    this.viewModel.ValidationComplete = null;
    this.viewModel.SelectedModuleId = null;
    this.viewModel.SelectedStateId = null;
    this.filename = null;
    this.viewModel.Top5Rows = null;
    this.viewModel.ImportLocation = null;
  }

  checkLength(importLocation: string): boolean {
    if (importLocation === null) {
      return false;
    }

    return importLocation.length >= 5;
  }

  get isImportButtonDisabled(): boolean {
    for (const additionalSettings of this.viewModel.AdditionalSettings) {
      const isMandatory = additionalSettings.Mandatory;
      const additionalValue =
        this.additionalValues[additionalSettings.PropertyName];
      if (isMandatory && additionalValue == undefined) {
        return true;
      }
    }
    return this.viewModel.Failed;
  }

  get isModuleInputRequired(): boolean {
    return this.importType === ImportType.HistoricalData;
  }

  get isValidateButtonDisabled(): boolean {
    return (
      this.importType === ImportType.HistoricalData && !this.selectedStateId
    );
  }

  get isValidateButtonDisplayed(): boolean {
    return (
      (this.viewModel?.Top5Rows &&
        this.viewModel?.Top5Rows.length > 0 &&
        !this.viewModel?.ValidationComplete) ||
      !!this.viewModel?.ValidationError
    );
  }

  get isImportButtonDisplayed(): boolean {
    return (
      this.viewModel?.ValidationComplete &&
      this.viewModel?.LevelCounts &&
      this.viewModel?.LevelCounts.length > 0
    );
  }
}
